import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StatCard from '../../components/StatCard';
import OnboardingSummary from '../../components/OnboardingSummary';
import TimesheetDownload from '../../components/TimesheetDownload';
import DocumentsToReview from '../../components/DocumentsToReview';
import { issuesAPI, onboardingAPI, performanceAPI, taskRequestsAPI, usersAPI } from '../../services/api';
import useRefreshOnFocus from '../../utils/useRefreshOnFocus';

const REQ_COLOR = { review: 'blue', meeting: 'purple', clarification: 'orange' };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [overview, setOverview] = useState([]);
  const [newEnquiries, setNewEnquiries] = useState(0);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [openIssues, setOpenIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    Promise.all([usersAPI.list(), performanceAPI.overview()])
      .then(([u, ov]) => { setUsers(u.data); setOverview(ov.data); })
      .finally(() => setLoading(false));
    onboardingAPI.list({ stage: 'tutor_suggestion' })
      .then(({ data }) => setNewEnquiries(data.length)).catch(() => {});
    taskRequestsAPI.list()
      .then(({ data }) => setPendingRequests(data.filter((r) => r.status === 'pending')))
      .catch(() => {});
    issuesAPI.list({ issue_status: 'open' })
      .then(({ data }) => setOpenIssues(data.slice(0, 8))).catch(() => {});
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useRefreshOnFocus(refresh);

  if (loading) return <div className="page-loading">Loading...</div>;

  const employees = users.filter((u) => u.role === 'employee');
  const hrs = users.filter((u) => u.role === 'hr');
  const admins = users.filter((u) => u.role === 'super_admin');
  const active = users.filter((u) => u.is_active);
  const totalTasks = overview.reduce((a, e) => a + e.total_tasks, 0);

  return (
    <div className="page">
      <div className="page-header">
        <h2>System Overview</h2>
        <Link to="/admin/users/new" className="btn btn-primary">+ Create User</Link>
      </div>

      <TimesheetDownload presets />

      <div className="stats-grid">
        <StatCard title="New Enquiries" value={newEnquiries} icon="🎓" color="teal"
          onClick={() => navigate('/onboarding?stage=tutor_suggestion')} />
        <StatCard title="Total Users" value={users.length} icon="👥" color="blue" />
        <StatCard title="Active Users" value={active.length} icon="✅" color="green" />
        <StatCard title="Employees" value={employees.length} icon="👤" color="purple" />
        <StatCard title="HR Users" value={hrs.length} icon="🏢" color="teal" />
        <StatCard title="Admins" value={admins.length} icon="🔑" color="orange" />
        <StatCard title="Total Tasks" value={totalTasks} icon="📋" color="blue" />
      </div>

      <div className="card mt-6">
        <div className="card-header">
          <h3>All Users</h3>
          <Link to="/admin/users" className="link-sm">Manage →</Link>
        </div>
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Username</th><th>Role</th><th>Department</th><th>Status</th><th>Created</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.full_name}</td>
                <td className="text-muted">@{u.username}</td>
                <td><span className={`badge badge-${u.role === 'super_admin' ? 'purple' : u.role === 'hr' ? 'blue' : 'green'}`}>{u.role.replace('_', ' ')}</span></td>
                <td>{u.department || '—'}</td>
                <td><span className={`badge badge-${u.is_active ? 'green' : 'red'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                <td>{new Date(u.created_at || Date.now()).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="dashboard-grid mt-6">
        <div className="card">
          <div className="card-header">
            <h3>Pending Requests {pendingRequests.length > 0 && <span className="badge badge-orange ml-2">{pendingRequests.length}</span>}</h3>
          </div>
          {pendingRequests.length === 0 ? (
            <p className="text-muted" style={{ padding: '12px 20px' }}>No pending requests.</p>
          ) : pendingRequests.map((r) => (
            <div key={r.id} className="issue-item" style={{ cursor: 'pointer' }} onClick={() => navigate(`/hr/tasks/${r.task_id}`)}>
              <span className={`badge badge-${REQ_COLOR[r.type] || 'blue'}`}>{r.type}</span>
              <span className="ml-2">Task #{r.task_id}</span>
              {r.notes && <span className="text-muted ml-2">— {r.notes.substring(0, 60)}</span>}
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Open Issues</h3>
            <Link to="/hr/issues" className="link-sm">View all →</Link>
          </div>
          {openIssues.length === 0 ? (
            <p className="text-muted" style={{ padding: '12px 20px' }}>No open issues.</p>
          ) : openIssues.map((iss) => (
            <div key={iss.id} className="issue-item">
              <span className={`badge badge-${iss.urgency === 'critical' ? 'red' : iss.urgency === 'high' ? 'orange' : 'blue'}`}>{iss.urgency}</span>
              <span className="ml-2">{iss.title}</span>
              <span className="text-muted ml-2">by {iss.raised_by?.full_name}</span>
            </div>
          ))}
        </div>
      </div>

      <DocumentsToReview />

      <OnboardingSummary />
    </div>
  );
}
